import React, { useState, useEffect, useCallback } from 'react';
import { Heading } from './common/Catalyst/heading';
import { useLocation, useNavigate } from 'react-router-dom';
import { ViolationReturn, ViolationUpdate } from './clients/rust-api-client';
import { homeService, violationService } from './clients/ApiClients';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import { ViolationFilterBar } from './violation/linear/ViolationFilterBar';
import { GroupedViolationList } from './violation/linear/GroupedViolationList';
import { saveListContext } from './violation/linear/listContext';
import { useRowUpdate } from './violation/linear/useRowUpdate';
import { useListSelection } from './violation/linear/useListSelection';
import { SelectionBar } from './violation/linear/RowCheckbox';
import { canCreateViolation, canEditViolation } from './constants/UserTypes';
import { useCommandPalette } from './shell/CommandPaletteProvider';
import { CreateButton } from './shell/CreateButton';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { useSyncedViolations } from './sync/hooks';
import { useFreshFines } from './sync/fines';
import { updateViolation } from './sync/writes';
import { useHoaDirectory } from './hoa/useHoaDirectory';
import {
  EMPTY_FILTERS,
  ViolationFilters,
  applyFilters,
  groupByStatus,
  normalizeSeverity,
  normalizeStatus,
} from './violation/linear/violationMeta';
import { apiErrorMessage } from './utils/apiErrors';

interface ViolationListProps {
  hoaId?: string | null;
  homeId?: string | null;
  memberId?: string | null;
  title?: string | null;
  /**
   * Fill the parent's height (the violations page): the list scrolls
   * inside its panel instead of growing the page.
   */
  fill?: boolean;
  /**
   * With `fill`: no frame of its own, for a page that runs the list to the
   * panel's edges (SidebarLayout `flush`, the violations page's list view).
   */
  flush?: boolean;
  /** Off where the page already names the list (the violations page). */
  showTitle?: boolean;
  /** Placed at the right end of the filter bar: the view switch. */
  toolbar?: React.ReactNode;
}

/**
 * Violations as a Linear-style grouped list (#603 step 2): status groups
 * with counts, filter chips, dense rows. Embedded wherever violations are
 * listed — the all-violations page and the HOA, home and member pages —
 * so the props decide the scope and this component decides the shape.
 */
const ViolationList: React.FC<ViolationListProps> = ({
  hoaId,
  homeId,
  memberId,
  title,
  fill = false,
  flush = false,
  showTitle = true,
  toolbar,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fetchedViolations, setViolations] = useState<ViolationReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchErrorTitle, setFetchErrorTitle] = useState<string>('');
  const [fetchError, setFetchError] = useState<string>('');
  const [showFetchError, setShowFetchError] = useState(false);
  const [filters, setFilters] = useState<ViolationFilters>(EMPTY_FILTERS);
  const [reloadKey, setReloadKey] = useState(0);
  const { openNewViolation } = useCommandPalette();

  // Status and severity change from the row itself (#603).
  const rowUpdate = useRowUpdate<ViolationReturn, ViolationUpdate>(
    useCallback(
      (violationId, violationUpdate) => updateViolation(violationId, violationUpdate),
      []
    ),
    useCallback(
      (updated) =>
        setViolations((prev) => prev.map((v) => (v.id === updated.id ? updated : v))),
      []
    )
  );
  const canEdit = canEditViolation(getUserTypeFromToken());
  const canCreate = canCreateViolation(getUserTypeFromToken());

  // Create from the list (#632), whatever its scope. An HOA's or a home's
  // list fixes those chips; a member's offers only that member's homes;
  // the all-violations list leaves them to pick.
  const handleCreate = () =>
    openNewViolation({
      ...(hoaId || homeId || memberId
        ? { scope: { hoaId, homeId, memberId }, lockScope: true }
        : {}),
      onCreated: (created) =>
        // Which of a member's homes it landed on decides whether it
        // belongs here, so a member's list asks again.
        memberId
          ? setReloadKey((k) => k + 1)
          : setViolations((prev) => [created, ...prev]),
    });

  // The sync engine's local copy when it has one (#561): the same scope as
  // the requests below, answered from memory and kept current. Fetching is
  // the fallback.
  const synced = useSyncedViolations({ hoaId, homeId, memberId });
  const fromStore = synced !== null;
  const violations = synced ?? fetchedViolations;
  // The fine chips: money is not in the copy, so it is read from REST in
  // the background and laid over the synced rows (`sync/fines.ts`).
  useFreshFines({ hoaId });

  useEffect(() => {
    const fetchViolations = async () => {
      if (fromStore) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        // Scoped to one HOA where we have one, otherwise everything the
        // caller is entitled to see. Both endpoints filter by role
        // server-side; getViolationsByHoa additionally 403s if this user
        // is not assigned to the HOA.
        const response = hoaId
          ? await violationService.getViolationsByHoa({ hoaId })
          : await violationService.getAllViolationsForUser();
        let allViolations = response.data.violations;

        // home_id is on the violation itself, so narrowing to one home
        // needs no extra request.
        if (homeId) {
          allViolations = allViolations.filter((v) => v.home_id === homeId);
        }
        // A violation is attached to a home, not to a member, so
        // narrowing to one member means resolving their homes first.
        // A member may own more than one.
        if (memberId) {
          const homes = await homeService.getHomesByMember({ memberId });
          const memberHomeIds = new Set(homes.data.map((h) => h.id));
          allViolations = allViolations.filter((v) =>
            memberHomeIds.has(v.home_id)
          );
        }

        setViolations(allViolations);
      } catch (err) {
        console.error('Error fetching violations:', err);
        const errorMessage = apiErrorMessage(err, "Couldn't load violations.");
        setFetchErrorTitle("Couldn't load violations");
        setFetchError(errorMessage);
        setShowFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchViolations();
  }, [hoaId, homeId, memberId, fromStore, reloadKey]);

  // Filter by HOA (#618) — only where the list spans HOAs. A violation's
  // HOA is its home's, so this needs the home → HOA directory.
  const spansHoas = !hoaId && !homeId && !memberId;
  const directory = useHoaDirectory({ enabled: spansHoas, withHomes: true });
  const hoaOf = (v: ViolationReturn) => directory.hoaOfHome(v.home_id);
  const hoaOptions = spansHoas
    ? Array.from(
        new Set(violations.map(hoaOf).filter((id): id is string => !!id))
      )
        .map((id) => ({ value: id, label: directory.hoaName(id) ?? id }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : undefined;

  const visibleViolations = applyFilters(violations, filters, hoaOf);
  const groups = groupByStatus(visibleViolations);

  // Checked rows: a change to one of them is made to all of them.
  const selection = useListSelection(visibleViolations);
  const handleRowUpdate = (v: ViolationReturn, patch: ViolationUpdate, field: string) => {
    const value = patch[field as keyof ViolationUpdate];
    const current = (row: ViolationReturn, f: string) =>
      f === 'severity' ? normalizeSeverity(row.severity) : normalizeStatus(row.status);
    rowUpdate.update(selection.targets(v, field, String(value), current), patch, field);
  };

  const handleRowClick = (violation: ViolationReturn) => {
    // Remember the list as it is shown — filtered and grouped — so the
    // detail page can step through it with ↑/↓ in the same order.
    saveListContext({
      ids: groups.flatMap((g) => g.violations.map((v) => v.id)),
      from: location.pathname,
    });
    navigate(`/violations/${violation.id}`);
  };

  // With a synced copy there is nothing to wait for: render it on the
  // first paint, not after the effect clears `loading` (#635).
  if (loading && !fromStore) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  return (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col' : undefined}>
      {showTitle && (
        <div className="mb-3">
          <Heading level={4}>
            {title || (homeId ? 'Home Violations' : 'Violations List')}
          </Heading>
        </div>
      )}
      {/* Flush against the panel's edges, alerts need their own inset. */}
      <div className={flush ? 'px-3 pt-3 empty:hidden' : undefined}>
        <ShowAlert
          alertType="error"
          alertTitle={fetchErrorTitle}
          alertMessage={fetchError}
          isVisible={showFetchError}
          onClose={() => setShowFetchError(false)}
          timeout={5000}
          isInline={true}
        />
        <ShowAlert
          alertType="error"
          alertTitle="Change not saved"
          alertMessage={rowUpdate.error ?? ''}
          isVisible={rowUpdate.error !== null}
          onClose={rowUpdate.clearError}
          timeout={8000}
          isInline={true}
        />
      </div>
      <div
        className={
          flush
            ? 'flex min-h-0 flex-1 flex-col'
            : fill
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-line bg-raised'
              : 'overflow-hidden rounded-lg border border-line bg-raised'
        }
      >
        <ViolationFilterBar
          filters={filters}
          onChange={setFilters}
          shown={visibleViolations.length}
          total={violations.length}
          hoaOptions={hoaOptions}
          trailing={
            <>
              {canCreate && <CreateButton label="New violation" onClick={handleCreate} />}
              {toolbar}
            </>
          }
        />
        {canEdit && (
          <SelectionBar count={selection.selected.size} onClear={selection.clear} />
        )}
        <div className={fill ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
          <GroupedViolationList
            groups={groups}
            onOpen={handleRowClick}
            editing={{
              canEdit,
              saving: rowUpdate.saving,
              onUpdate: handleRowUpdate,
              selection,
            }}
            emptyMessage={
              violations.length === 0
                ? 'No violations yet.'
                : 'No violations match these filters.'
            }
          />
        </div>
      </div>

    </div>
  );
};

export default ViolationList;
